import { type FC, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../../lib/utils";
import { inputSize } from "./inputSize";

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> & {
	compact?: boolean;
	rightAlign?: boolean;
	error?: boolean;
	containerClassName?: string;
	prefix?: ReactNode;
	suffix?: ReactNode;
	testId?: string;
	ref?: Ref<HTMLInputElement>;
};

export const Input: FC<InputProps> = ({
	className,
	containerClassName,
	type = "text",
	compact,
	rightAlign,
	error = false,
	disabled,
	value,
	prefix,
	suffix,
	testId,
	ref,
	...props
}) => {
	const { affix, frame, paddingX, paddingY } = inputSize(compact);
	return (
		<div
			className={cn(
				"relative flex w-full rounded-md outline outline-[1px] outline-offset-[-1px]",
				"text-primary outline-bc-strong bg-surface-light/70",
				"placeholder:text-tertiary/85 focus-within:outline-[2px]",
				"focus-within:outline-text-secondary focus-within:hover:outline-text-secondary",
				frame,
				disabled ? "cursor-not-allowed opacity-50" : "hover:outline-text-tertiary",
				error &&
					"outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2",
				containerClassName,
			)}
		>
			{prefix && (
				<div
					className={cn(
						"h-full flex items-center rounded-l-md text-secondary bg-surface-neutral border-r border-r-bc-strong whitespace-nowrap flex-shrink-0",
						affix,
						disabled && "text-tertiary",
					)}
				>
					{prefix}
				</div>
			)}
			<input
				type={type}
				value={value ?? ""}
				disabled={disabled}
				tabIndex={0}
				ref={ref}
				className={cn(
					"w-full outline-none bg-transparent",
					paddingX,
					paddingY,
					rightAlign && "text-right",
					className,
				)}
				{...props}
				// Needed to disable browser auto-filling otherwise 1password will try to autofill every field
				autoComplete="one-time-code"
				data-1p-ignore
				data-lpignore
				data-testid={testId}
			/>
			{suffix && (
				<div
					className={cn(
						"h-full flex items-center rounded-r-md text-secondary bg-surface-neutral border-l border-l-bc-strong whitespace-nowrap flex-shrink-0",
						affix,
						disabled && "text-tertiary",
					)}
				>
					{suffix}
				</div>
			)}
		</div>
	);
};
